import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getNotificationHistory, clearNotificationHistory, markAsRead } from '../utils/notificationHistory';
import { NotificationHistoryItem } from '../types';
import { ChevronDown, CalendarDays, Clock3, MonitorSmartphone } from 'lucide-react';
import { getFlagUrl } from '../utils/flags';

const dateKey = (ts: number) => {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${da}`;
};

const dateLabel = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, (m - 1), d).toLocaleDateString('ru-RU', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const hourKey = (ts: number) => {
  const d = new Date(ts);
  return String(d.getHours()).padStart(2, '0');
};

const groupHistory = (items: NotificationHistoryItem[]) => {
  const byDate: Record<string, NotificationHistoryItem[]> = {};
  for (const it of items) {
    const k = dateKey(it.timestamp);
    if (!byDate[k]) byDate[k] = [];
    byDate[k].push(it);
  }
  const result = Object.entries(byDate)
    .map(([date, arr]) => {
      const byHour: Record<string, NotificationHistoryItem[]> = {};
      for (const it of arr) {
        const h = hourKey(it.timestamp);
        if (!byHour[h]) byHour[h] = [];
        byHour[h].push(it);
      }
      const hours = Object.entries(byHour)
        .sort((a, b) => Number(b[0]) - Number(a[0]))
        .map(([hour, list]) => ({ hour, list: list.sort((x, y) => y.timestamp - x.timestamp) }));
      return { date, hours };
    })
    .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
  return result;
};

const NotificationsHistory: React.FC = () => {
  const [items, setItems] = useState<NotificationHistoryItem[]>([]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [devicesExpanded, setDevicesExpanded] = useState<Record<string, boolean>>({});

  const reload = async () => setItems(await getNotificationHistory());

  useEffect(() => {
    reload();
    const onUpd = () => reload();
    window.addEventListener('notification-history-updated', onUpd);
    // Добавляем перезагрузку и на событие обновления непрочитанных
    window.addEventListener('notification-unread-updated', onUpd);
    return () => {
      window.removeEventListener('notification-history-updated', onUpd);
      window.removeEventListener('notification-unread-updated', onUpd);
    };
  }, []);

  // При загрузке страницы сразу запускаем проверку видимости
  useEffect(() => {
    // Небольшая задержка, чтобы DOM успел отрендериться
    const timer = setTimeout(() => {
      const check = async () => {
        const toRead: string[] = [];
        const readSet = new Set(items.filter(i => i.read).map(i => i.id));
        document.querySelectorAll('[data-nid]')?.forEach((el) => {
          const id = (el as HTMLElement).dataset['nid'];
          if (!id || readSet.has(id)) return;
          const rect = (el as HTMLElement).getBoundingClientRect();
          const vh = window.innerHeight || document.documentElement.clientHeight;
          const vw = window.innerWidth || document.documentElement.clientWidth;
          if (rect.width <= 0 || rect.height <= 0) return;
          const interW = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
          const interH = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
          const interArea = interW * interH;
          const area = rect.width * rect.height;
          if (area > 0 && interArea / area >= 0.1) {
            toRead.push(id);
          }
        });
        if (toRead.length > 0) {
          setItems((prev) => prev.map((it) => (toRead.includes(it.id) ? { ...it, read: true } : it)));
          await markAsRead(toRead);
        }
      };
      check();
    }, 900);
    return () => clearTimeout(timer);
  }, [items]);
  // Триггер перерендера при изменении прочитанности
  const [, setReadTick] = useState(0);
  const visibleSinceRef = useRef<Map<string, number>>(new Map());
  const READ_DELAY_MS = 900; // задержка перед автопрочтением
  useEffect(() => {
    const onRU = () => setReadTick((x) => x + 1);
    window.addEventListener('notification-unread-updated', onRU);
    return () => window.removeEventListener('notification-unread-updated', onRU);
  }, []);

  // Помечаем видимые элементы как прочитанные (scroll/resize/click + на обновление items)
  useEffect(() => {
    const isVisibleHalf = (el: Element) => {
      const rect = (el as HTMLElement).getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      if (rect.width <= 0 || rect.height <= 0) return false;
      const interW = Math.max(0, Math.min(rect.right, vw) - Math.max(rect.left, 0));
      const interH = Math.max(0, Math.min(rect.bottom, vh) - Math.max(rect.top, 0));
      const interArea = interW * interH;
      const area = rect.width * rect.height;
      // Достаточно 10% площади, чтобы считать карточку увиденной
      return area > 0 && interArea / area >= 0.1;
    };
    const check = async () => {
      const toRead: string[] = [];
      const now = Date.now();
      // Используем локальное состояние items для определения прочитанности
      const readSet = new Set(items.filter(i => i.read).map(i => i.id));
      document.querySelectorAll('[data-nid]')?.forEach((el) => {
        const id = (el as HTMLElement).dataset['nid'];
        if (!id) return;
        if (readSet.has(id)) {
          // уже прочитано — очищаем таймер
          visibleSinceRef.current.delete(id);
          return;
        }
        const visible = isVisibleHalf(el);
        if (visible) {
          const started = visibleSinceRef.current.get(id);
          if (started == null) {
            visibleSinceRef.current.set(id, now);
          } else if (now - started >= READ_DELAY_MS) {
            toRead.push(id);
            visibleSinceRef.current.delete(id);
          }
        } else {
          // вышло из зоны видимости — сбрасываем таймер
          visibleSinceRef.current.delete(id);
        }
      });
      if (toRead.length > 0) {
        // Локально отмечаем сразу, чтобы UI обновился мгновенно
        setItems((prev) => prev.map((it) => (toRead.includes(it.id) ? { ...it, read: true } : it)));
        // Отправляем батч на сервер (дебаунс внутри утилиты)
        await markAsRead(toRead);
      }
    };
    const onScrollOrResize = () => {
      // Используем rAF, чтобы попасть после отрисовки
      // Не дёргаем сеть на каждый скролл: check меняет только локальную видимость,
      // а серверный вызов происходит лишь при фактической пометке toRead > 0
      requestAnimationFrame(() => { check(); });
    };
    const onClick = () => {
      // После разворота секции даём верстке анимироваться
      requestAnimationFrame(() => setTimeout(check, 50));
    };
    const onHistoryUpdated = () => {
      requestAnimationFrame(() => setTimeout(check, 0));
    };
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('click', onClick, true);
    window.addEventListener('notification-history-updated', onHistoryUpdated as any);
    // Также попробуем слушать контейнер, если он скроллится
    const container = document.getElementById('notifications-scroll');
    if (container) container.addEventListener('scroll', onScrollOrResize, { passive: true } as any);
    // Запуск сразу после изменения данных
    requestAnimationFrame(() => { check(); });
    return () => {
      window.removeEventListener('scroll', onScrollOrResize as any);
      window.removeEventListener('resize', onScrollOrResize as any);
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('notification-history-updated', onHistoryUpdated as any);
      if (container) container.removeEventListener('scroll', onScrollOrResize as any);
    };
  }, [items]);

  // Убрано дублирование: используем единый механизм read через scroll/resize/click

  // Убрали автосидирование: страница не инициирует проверку заказов

  const grouped = useMemo(() => groupHistory(items), [items]);
  const todayKey = dateKey(Date.now());
  const currentHour = hourKey(Date.now());

  return (
    <div className="px-8 py-8 bg-[#181A20] min-h-screen" id="notifications-scroll">
      <div className="mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <h1 className="text-3xl font-bold text-white tracking-wide">Уведомления</h1>
          {items.length > 0 ? (
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <button
                className="w-full sm:w-auto px-4 py-3 text-sm font-medium text-white bg-[#353945] rounded-lg hover:bg-[#2A2D36]"
                onClick={async () => {
                  try {
                    const ids = items.map((i)=>i.id);
                    await markAsRead(ids);
                    // Локально отмечаем всё как прочитанное
                    setItems((prev) => prev.map((it) => ({ ...it, read: true })));
                  } catch {}
                }}
              >
                Отметить прочитанным все
              </button>
              <button
                className="w-full sm:w-auto px-4 py-3 text-sm font-medium text-white bg-[#353945] rounded-lg hover:bg-[#2A2D36]"
                onClick={async () => { await clearNotificationHistory(); }}
              >
                Очистить историю
              </button>
            </div>
          ) : null}
        </div>

        {items.length === 0 ? (
          <div className="text-[#B1B5C3]">Пока нет уведомлений</div>
        ) : (
          <div className="space-y-4">
            {grouped.map(({ date, hours }) => {
              const readSetDay = new Set(items.filter(i => i.read).map(i => i.id));
              const dayUnread = hours.some((h) => h.list.some((it) => !readSetDay.has(it.id)));
              const dayKey = `day_${date}`;
              const isDayOpen = (dayKey in expanded) ? !!expanded[dayKey] : (date === todayKey);
              return (
                <div key={date} className="bg-[#181A20] rounded-xl shadow border border-[#23262F]">
                  <button
                    className="w-full px-6 py-3 border-b border-[#23262F] flex items-center justify-between text-white"
                    onClick={() => setExpanded({ ...expanded, [dayKey]: !isDayOpen })}
                  >
                    <span className="flex items-center gap-2 font-semibold">
                      <ChevronDown size={18} className={`transition-transform ${isDayOpen ? 'rotate-180' : 'rotate-0'}`} />
                      <CalendarDays size={16} className="text-[#B1B5C3]" />
                      {dateLabel(date)}
                      {date === todayKey ? (
                        <span className="ml-1 inline-flex items-center">
                          <span className="relative inline-flex h-2.5 w-2.5">
                            <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                          </span>
                        </span>
                      ) : null}
                    </span>
                    <span className="flex items-center gap-2 text-xs text-[#B1B5C3]">
                      {hours.reduce((acc, h) => acc + h.list.length, 0)} уведомл.
                      {dayUnread ? <span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> : null}
                    </span>
                  </button>
                  {isDayOpen ? (
                    <div className="p-4 space-y-2">
                      {hours.map(({ hour, list }) => {
                        const key = `${date}_${hour}`;
                        const isOpen = (key in expanded) ? !!expanded[key] : (date === todayKey && hour === currentHour);
                        const readSetH = new Set(items.filter(i => i.read).map(i => i.id));
                        const hourUnread = list.some((it) => !readSetH.has(it.id));
                        return (
                          <div key={key} className="border border-[#23262F] rounded-lg">
                            <button
                              className="w-full px-4 py-2 flex items-center justify-between text-white"
                              onClick={() => setExpanded({ ...expanded, [key]: !isOpen })}
                            >
                              <span className="flex items-center gap-2 font-medium">
                                <ChevronDown size={16} className={`transition-transform ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
                                <Clock3 size={14} className="text-[#B1B5C3]" />
                                {hour}:00
                                {date === todayKey && hour === currentHour ? (
                                  <span className="ml-1 inline-flex items-center">
                                    <span className="relative inline-flex h-2.5 w-2.5">
                                      <span className="absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75 animate-ping"></span>
                                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500"></span>
                                    </span>
                                  </span>
                                ) : null}
                              </span>
                              <span className="flex items-center gap-2 text-xs text-[#B1B5C3]">{list.length}{hourUnread ? <span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> : null}</span>
                            </button>
                            {isOpen ? (
                              <div className="divide-y divide-[#23262F]">
                                {list.map((it) => {
                                  const flagUrl = getFlagUrl(it.app_id);
                                  const isUnread = !it.read;
                                  const deviceNames = Array.isArray((it as any).device_names) ? ((it as any).device_names as string[]) : [];
                                  const showDevices = !!devicesExpanded[it.id];
                                  return (
                                    <div key={it.id} data-nid={it.id} className={`${isUnread ? 'bg-[#1C1E26]' : ''}`}>
                                      <div className="px-4 py-3 flex items-start gap-3">
                                        {/* Левая иконка приложения (фикс. высота, выравнивание по верхней границе) */}
                                        {it.app_id ? (
                                          <div className="w-16 sm:w-20 flex-none self-start">
                                            <img
                                              src={`https://app.rewardguru.limited/api-5.0/app_logo?app_id=${it.app_id}&img_size=Logo2`}
                                              alt="app"
                                              className="h-16 sm:h-20 w-full rounded object-contain object-top"
                                            />
                                          </div>
                                        ) : null}
                                        {/* Текстовая часть */}
                                        <div className="flex-1 min-w-0">
                                          <div className="text-white font-semibold truncate flex items-center gap-2">
                                            {it.title}
                                            {isUnread ? <span className="inline-block w-2 h-2 rounded-full bg-blue-500" /> : null}
                                          </div>
                                          <div className="text-[#B1B5C3] whitespace-pre-line text-sm">{it.body}</div>
                                          <div className="text-xs text-[#777E90] mt-1">{new Date(it.timestamp).toLocaleString('ru-RU')}</div>
                                        </div>
                                        {/* Правый флаг (фикс. высота, выравнивание по верхней границе) */}
                                        {flagUrl ? (
                                          <div className="w-10 sm:w-12 flex-none self-start ml-1">
                                            <img
                                              src={flagUrl}
                                              alt="flag"
                                              className="w-full h-auto max-h-16 sm:max-h-20 rounded-sm object-contain object-top"
                                            />
                                          </div>
                                        ) : null}
                                      </div>

                                      {deviceNames.length > 0 ? (
                                        <>
                                          <button
                                            type="button"
                                            className="w-full px-4 py-2 border-t border-[#23262F] flex items-center justify-between text-[#B1B5C3] hover:bg-[#1C1E26]"
                                            onClick={() => setDevicesExpanded((prev) => ({ ...prev, [it.id]: !prev[it.id] }))}
                                          >
                                            <span className="flex items-center gap-2 font-medium">
                                              <MonitorSmartphone size={16} className="text-[#777E90]" />
                                              Устройства
                                            </span>
                                            <ChevronDown size={16} className={`transition-transform ${showDevices ? 'rotate-180' : 'rotate-0'}`} />
                                          </button>
                                          {showDevices ? (
                                            <div className="px-4 pb-3 pt-2 text-sm text-[#B1B5C3] break-words">
                                              {deviceNames.join(', ')}
                                            </div>
                                          ) : null}
                                        </>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsHistory;


